import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  computed,
  onMounted,
  onActivated,
  onDeactivated,
  inject,
} from "vue";
import router from "@/router";
import { useUserStore } from "@/stores/userStore";
import { useLineStore } from "@/stores/lineStore";
import { ILineInfoData, ILineInfoLite } from "@/domain/entities/lineInfo";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import LineInfoRow from "@/components/line/LineInfoRow.vue";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import LineSearchFilterSummary from "@/components/line/LineSearchFilterSummary.vue";
import LineSearchFilter from "@/components/line/LineSearchFilter.vue";
import { IOption } from "@/domain/entities/filter/filter.types";
import { useHandleScrollPosition } from "@/composables/shared/useHandleScrollPosition";

import MultipleLineTurnPathMap from "@/components/map/MultipleLineTurnPathMap.vue";
import { useCheckedLineIds } from "@/composables/line/useCheckedLineIds.ts";
import { useGetCheckedLineDetails } from "@/composables/line/useGetCheckedLineDetails";
import { useServiceStore } from "@/stores/serviceStore";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";
import dayjs from "dayjs";


const __default__ = {
  name: "LineSearch",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const serviceStore = useServiceStore();
const userStore = useUserStore();
const lineStore = useLineStore();
const bookRequiredType = computed(() => lineStore.getBookRequiredType());

const searchedLineInfos = ref<ILineInfoLite[]>([]);

const lineSearchFilter = ref<typeof LineSearchFilter>();
const searchParamsMap = ref<Map<string, IOption | IOption[] | null>>(new Map());
const searchInput = ref<HTMLInputElement>();
const searchKeyword = ref("");
const isSearching = ref(false);
const hasSearched = ref(false);

const updatedSearchKeyword = (value: string) => {
  if (searchResultContainer.value) {
    searchResultContainer.value.scrollTop = 0;
  }
  searchKeyword.value = value.trim();
};

const shouldShowTurnPath = serviceStore.serviceInfo?.shouldShowTurnPath;

const pathMapBtnTitle = computed(() => {
  const count = `${checkedLineCount.value} / ${maxCheckedLinesCount.value}`;
  const title = shouldShowTurnPath ? "경로보기" : "지도보기";
  return `${title} ${count}`;
});

const shouldShowLineFilter = ref(false);
const toggleShowLineFilter = (show: boolean) => {
  shouldShowLineFilter.value = show;
};

const onSelectLine = (lineInfo: ILineInfoLite) => {
  searchInput.value?.blur();
  userStore.updateSelectedLineNum(lineInfo.lineNum);
  router.push({
    name: RouterName.LineInfoDetail,
    params: {
      lineId: lineInfo.id,
    },
  });
};

const goToNewRouteApplicationList = () => {
  searchInput.value?.blur();
  router.push({ name: RouterName.NewRouteApplicationList });
};

// 로컬스토리지에서 필터 로드
const loadSavedFilters = () => {
  try {
    const savedData = localStorage.getItem("savedLineFilters");
    const newMap = new Map<string, IOption | IOption[] | null>();

    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // 날짜 필터와 검색어 제외하고 복원
      Object.entries(parsedData).forEach(([key, value]) => {
        if (key !== "driveDate" && key !== "searchText") {
          newMap.set(key, value as IOption | IOption[] | null);
        }
      });
    }

    // 항상 오늘 날짜를 기본값으로 추가
    newMap.set("driveDate", {
      key: "today",
      name: "오늘",
      value: dayjs().format("YYYY-MM-DD"),
      default: true,
    });

    searchParamsMap.value = newMap;
  } catch (error) {
    console.error("필터 로드 실패:", error);
  }
};

// 로컬스토리지에 필터 저장
const saveFiltersToLocalStorage = (
  filters: Map<string, IOption | IOption[] | null>
) => {
  try {
    const dataToSave: Record<string, any> = {};

    // 날짜 필터와 검색어 제외하고 저장 (날짜는 항상 오늘로 설정되어야 함)
    filters.forEach((value, key) => {
      if (key !== "driveDate" && key !== "searchText") {
        dataToSave[key] = value;
      }
    });

    localStorage.setItem("savedLineFilters", JSON.stringify(dataToSave));
  } catch (error) {
    console.error("필터 저장 실패:", error);
  }
};

const handleSearch = async () => {
  if (isSearching.value) return;

  isSearching.value = true;
  hasSearched.value = true;

  if (searchResultContainer.value) {
    searchResultContainer.value.scrollTop = 0;
  }

  const sortOrder = serviceStore.serviceInfo?.lineSortOrder;

  // API 파라미터 구성
  let apiParams = `searchText=${encodeURIComponent(
    searchKeyword.value.trim()
  )}`;
  const filterParams = buildFilterApiParams();
  if (filterParams) {
    apiParams += `&${filterParams}`;
  }

  showLoadingImage();

  const fetchResult = await lineStore.getAllLineInfosLiteByFilters(
    apiParams,
    sortOrder
  );

  removeLoadingImage();
  isSearching.value = false;

  if (!fetchResult.isSuccess) {
    showErrorModal("노선 검색 실패", fetchResult.errorMessage);
    return;
  }

  searchedLineInfos.value = fetchResult.data ?? [];
};

const buildFilterApiParams = (): string => {
  const paramEntries: string[] = [];

  // 필터 타입에 따른 값 선택 매핑
  const filterTypeMapping: Record<string, "key" | "value"> = {
    driveDate: "value", // 날짜는 실제 날짜 값 (2025-01-25)
    beginStationTimeFrom: "value", // 시간은 실제 시간 값 (09:00)
    beginStationTimeTo: "value", // 시간은 실제 시간 값 (18:00)
    campusCodes: "key", // 캠퍼스는 키 값
    lineTimeCodes: "key", // 노선형태는 키 값
    lineDirectionCodes: "key", // 방향은 키 값
    lineDistrictCodes: "key", // 지역은 키 값
    bookYn: "key", // 예약은 키 값
    weekdayCodes: "key", // 요일은 키 값
  };

  searchParamsMap.value.forEach((value, key) => {
    if (key && value && key !== "searchText") {
      const valueType = filterTypeMapping[key] || "key";

      if (Array.isArray(value)) {
        const values = value
          .map((v) => v[valueType])
          .filter((v) => v !== undefined && v !== "");
        if (values.length > 0) {
          paramEntries.push(`${key}=${values.join(",")}`);
        }
      } else {
        const paramValue = value[valueType];
        // 전체날짜인 경우 (key="all") 빈 문자열로 처리
        if (key === "driveDate" && value.key === "all") {
          paramEntries.push(`${key}=`);
        } else if (paramValue !== undefined && paramValue !== "") {
          paramEntries.push(`${key}=${paramValue}`);
        }
      }
    }
  });

  return paramEntries.join("&");
};

const searchLine = async (
  searchParams: Map<string, IOption | IOption[] | null>,
  apiParams: string
) => {
  // 필터 업데이트
  searchParamsMap.value = new Map(searchParams);
  toggleShowLineFilter(false);

  // 키워드 + 필터 노선 검색
  handleSearch();

  // 로컬스토리지에 저장
  // saveFiltersToLocalStorage(searchParams);
};

const deleteFilter = (key: string) => {
  lineSearchFilter.value?.deleteSearchParam(key);
};

// 필터 검증 완료 후 처리
const handleFiltersValidated = (
  validatedSearchParams: Map<string, IOption | IOption[] | null>
) => {
  // LineSearchFilterSummary가 업데이트되도록 searchParamsMap 동기화
  searchParamsMap.value = new Map(validatedSearchParams);
};

const searchResultContainer = ref<HTMLElement>();

const { setCurrentScrollTop, moveToCurrentScrollTop } =
  useHandleScrollPosition();

/** 경로 비교할 노선 최대 개수 */
const maxCheckedLinesCount = computed(
  () => serviceStore.serviceInfo?.linePathMapInfo.maxLinePathSelectCount ?? 0
);

const linePathColorInfos = computed(
  () => serviceStore.serviceInfo?.linePathMapInfo.pathColors ?? []
);

const {
  checkedLineIds,
  checkedLineCount,
  isPossibleToCheckLine,
  isPossibleToShowLinePath,
  handleCheckLineInfoRow,
} = useCheckedLineIds(maxCheckedLinesCount.value);

const { result: lineInfosResult, fetchCheckedLineDetails } =
  useGetCheckedLineDetails();

const shouldShowMultipleLineTurnPathMap = ref(false);

const toggleShowMultipleLineTurnPathMap = (show: boolean) => {
  shouldShowMultipleLineTurnPathMap.value = show;
};

const handleClickShowLineTurnPathMapButton = async () => {
  await fetchCheckedLineDetails(checkedLineIds.value);
  // await setIntervalCallback(fetchMultipleLinesLocation, true);

  // 타입 좁히기 - API를 쏘지 않았을 때만 undefined
  if (!lineInfosResult.value) return;

  if (!lineInfosResult.value.isSuccess) {
    const { errorMessage, errorTime, resultCode } = lineInfosResult.value;

    showErrorModal(
      "체크된 노선 상세조회 오류",
      errorMessage,
      resultCode,
      errorTime
    );

    return;
  }

  toggleShowMultipleLineTurnPathMap(true);
};

// const defaultSearchParamsMap = new Map([
//   [
//     "driveDate",
//     {
//       default: true,
//       key: "today",
//       name: "오늘",
//       value: dayjs().format("YYYY-MM-DD"),
//     },
//   ],
// ]);

// const defaultApiParams = `driveDate=${dayjs().format("YYYY-MM-DD")}`;

onMounted(() => {
  // 저장된 필터 로드
  loadSavedFilters();

  setTimeout(() => {
    searchInput.value?.focus();
  }, 200);
});

onActivated(() => {
  moveToCurrentScrollTop(searchResultContainer.value);

  setTimeout(() => {
    searchInput.value?.focus();
  }, 200);
});

onDeactivated(() => {
  setCurrentScrollTop(searchResultContainer.value);

  setTimeout(() => {
    searchInput.value?.blur();
  }, 200);
});

const __returned__ = { showLoadingImage, removeLoadingImage, showErrorModal, serviceStore, userStore, lineStore, bookRequiredType, searchedLineInfos, lineSearchFilter, searchParamsMap, searchInput, searchKeyword, isSearching, hasSearched, updatedSearchKeyword, shouldShowTurnPath, pathMapBtnTitle, shouldShowLineFilter, toggleShowLineFilter, onSelectLine, goToNewRouteApplicationList, loadSavedFilters, saveFiltersToLocalStorage, handleSearch, buildFilterApiParams, searchLine, deleteFilter, handleFiltersValidated, searchResultContainer, setCurrentScrollTop, moveToCurrentScrollTop, maxCheckedLinesCount, linePathColorInfos, checkedLineIds, checkedLineCount, isPossibleToCheckLine, isPossibleToShowLinePath, handleCheckLineInfoRow, lineInfosResult, fetchCheckedLineDetails, shouldShowMultipleLineTurnPathMap, toggleShowMultipleLineTurnPathMap, handleClickShowLineTurnPathMapButton, Page, NavigationBar, LineInfoRow, SearchInput, LineSearchFilterSummary, LineSearchFilter, MultipleLineTurnPathMap, ScrollTopControl, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})